import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchOwnerEvents,
  refreshCalendars,
  setMovable,
  type OwnerEvent,
} from "../api.js";

/**
 * The owner's merged calendar (1s-1w).
 *
 * Every block is attributed to the account it came from, by colour *and* by an
 * organization label - colour alone would fail anyone who cannot distinguish
 * two of the seven sources, and with several work accounts "whose calendar is
 * this?" is the question the screen exists to answer.
 *
 * This is also where a meeting is marked movable, because it is the only place
 * an owner sees their week as one thing.
 */

function dayKey(iso: string, zone: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    timeZone: zone,
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function timeRange(event: OwnerEvent, zone: string): string {
  if (event.isAllDay) return "All day";
  const options: Intl.DateTimeFormatOptions = {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
  };
  return `${new Date(event.startUtc).toLocaleTimeString(undefined, options)} – ${new Date(
    event.endUtc,
  ).toLocaleTimeString(undefined, options)}`;
}

export function Calendar({ timezone }: { timezone: string }) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState(7);
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  const range = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    return {
      from: from.toISOString(),
      to: new Date(from.getTime() + days * 864e5).toISOString(),
    };
  }, [days]);

  const events = useQuery({
    queryKey: ["events", range.from, range.to],
    queryFn: () => fetchOwnerEvents(range.from, range.to),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["events"] });
    void queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const flag = useMutation({ mutationFn: setMovable, onSuccess: invalidate });
  const refresh = useMutation({ mutationFn: refreshCalendars, onSuccess: invalidate });

  const all = events.data?.events ?? [];
  const grouped = new Map<string, OwnerEvent[]>();
  for (const event of all) {
    const key = dayKey(event.startUtc, timezone);
    grouped.set(key, [...(grouped.get(key) ?? []), event]);
  }

  // The legend is built from what is actually on screen, so it never lists a
  // source the owner cannot see.
  const sources = new Map<string, number>();
  for (const event of all) sources.set(event.organization, event.colorIndex);

  return (
    <section className="card wide">
      <h1>Calendar</h1>
      <p className="lede">
        Everything counted as busy, from every connected calendar, in {timezone}.
      </p>

      <div className="row">
        {[7, 14, 28].map((value) => (
          <button
            key={value}
            type="button"
            className={days === value ? "chip chip-on" : "chip"}
            aria-pressed={days === value}
            onClick={() => setDays(value)}
          >
            {value} days
          </button>
        ))}
        <button
          type="button"
          className="ghost"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? "Refreshing…" : "Refresh calendars"}
        </button>
      </div>

      {sources.size > 0 ? (
        <div className="legend">
          {[...sources.entries()].map(([organization, colorIndex]) => (
            <span key={organization} className="legenditem">
              <span
                className="source-dot"
                style={{ background: `var(--source-${String(colorIndex + 1)})` }}
                aria-hidden="true"
              />
              {organization}
            </span>
          ))}
        </div>
      ) : null}

      {events.isPending ? <p className="helper">Loading your week…</p> : null}
      {!events.isPending && all.length === 0 ? (
        <p className="helper">
          Nothing busy in this range — or nothing has been read yet. Try refreshing.
        </p>
      ) : null}

      {[...grouped.entries()].map(([day, dayEvents]) => (
        <section key={day} className="dayblock">
          <h2>{day}</h2>
          {dayEvents.map((event) => (
            <div
              key={event.id}
              className={event.movable ? "event event-movable" : "event"}
            >
              <span
                className="source-bar"
                style={{ background: `var(--source-${String(event.colorIndex + 1)})` }}
                aria-hidden="true"
              />
              <div className="event-body">
                <strong>{event.title ?? "Busy"}</strong>
                <span className="helper">
                  {timeRange(event, timezone)} · {event.organization} ·{" "}
                  {event.calendarName}
                  {event.movable
                    ? event.movableSeries
                      ? " · movable series"
                      : " · movable"
                    : ""}
                </span>
              </div>

              {event.providerEventId ? (
                <button
                  type="button"
                  className="ghost"
                  aria-expanded={openEvent === event.id}
                  onClick={() => setOpenEvent(openEvent === event.id ? null : event.id)}
                >
                  {event.movable ? "Movable" : "Mark movable"}
                </button>
              ) : null}

              {openEvent === event.id && event.providerEventId ? (
                <div className="popover">
                  <p className="helper">
                    Marking a meeting movable lets Meet With suggest shifting it to fit a
                    request. The flag is stored here only — nothing changes in Outlook or
                    Google, and nothing moves unless you move it.
                  </p>
                  <div className="row">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        flag.mutate({
                          providerEventId: event.providerEventId!,
                          scope: "occurrence",
                          movable: !event.movable || event.movableSeries,
                        })
                      }
                    >
                      {event.movable && !event.movableSeries
                        ? "Not movable"
                        : "This occurrence"}
                    </button>
                    {event.seriesMasterId ? (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() =>
                          flag.mutate({
                            providerEventId: event.providerEventId!,
                            seriesMasterId: event.seriesMasterId,
                            scope: "series",
                            movable: !event.movableSeries,
                          })
                        }
                      >
                        {event.movableSeries ? "Series not movable" : "The whole series"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => setOpenEvent(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </section>
      ))}
    </section>
  );
}
